import {
  forwardRef,
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
  useRef,
  createRef,
} from 'react'
import { createPortal } from 'react-dom'
import { classNames } from '@/ui/utils/main'
import { ChevronDown, Check } from 'lucide-react'

interface SelectContextType {
  value?: string
  onValueChange?: (value: string) => void
  open: boolean
  setOpen: (open: boolean) => void
  registerLabel: (value: string, label: string) => void
  getLabel: (value: string) => string | undefined
  triggerRef: React.RefObject<HTMLButtonElement | null>
}

const SelectContext = createContext<SelectContextType>({
  open: false,
  setOpen: () => {},
  registerLabel: () => {},
  getLabel: () => undefined,
  triggerRef: createRef<HTMLButtonElement>(),
})

const Select = ({
  children,
  value,
  onValueChange,
  disabled,
}: {
  children: React.ReactNode
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
}) => {
  const [open, setOpen] = useState(false)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const triggerRef = useRef<HTMLButtonElement>(null)

  const registerLabel = useCallback((value: string, label: string) => {
    setLabels((prev) => (prev[value] === label ? prev : { ...prev, [value]: label }))
  }, [])

  const getLabel = useCallback((value: string) => labels[value], [labels])

  return (
    <SelectContext.Provider
      value={{ value, onValueChange, open, setOpen, registerLabel, getLabel, triggerRef }}
    >
      <div
        className="relative"
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev)
        }}
      >
        {children}
      </div>
    </SelectContext.Provider>
  )
}

const SelectTrigger = forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement> & { className?: string }
>(({ className, children, ...props }, fwdRef) => {
  const { open, triggerRef } = useContext(SelectContext)
  return (
    <button
      ref={(node: HTMLButtonElement | null) => {
        if (triggerRef && node) triggerRef.current = node
        if (typeof fwdRef === 'function') fwdRef(node)
        else if (fwdRef && node) fwdRef.current = node
      }}
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      className={classNames(
        'flex items-center justify-between gap-2 h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown
        className={classNames('w-4 h-4 opacity-50 transition-transform', open && 'rotate-180')}
      />
    </button>
  )
})
SelectTrigger.displayName = 'SelectTrigger'

const SelectValue = ({ placeholder }: { placeholder?: string }) => {
  const { value, getLabel } = useContext(SelectContext)
  const label = value ? getLabel(value) : undefined
  return (
    <span className={classNames(!value && 'text-muted-foreground')}>
      {value ? (label ?? value) : (placeholder ?? 'Select...')}
    </span>
  )
}

const SelectContent = ({ children }: { children: React.ReactNode }) => {
  const { open, triggerRef } = useContext(SelectContext)
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  )

  useEffect(() => {
    if (!open || !triggerRef.current) return

    const updatePosition = () => {
      const current = triggerRef.current
      if (!current) return
      const rect = current.getBoundingClientRect()
      // `position: fixed` is viewport-relative already — `getBoundingClientRect()`
      // is too, so adding `window.scrollY`/`scrollX` here double-counts the
      // scroll offset and pushes the dropdown further down/right the more the
      // (document-scrolling) page has been scrolled.
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, triggerRef])

  const content = (
    <div
      className="fixed z-50 rounded-md border bg-popover text-popover-foreground shadow-lg"
      style={
        open && position
          ? {
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              marginTop: '4px',
            }
          : { display: 'none' }
      }
    >
      {/* Always rendered so SelectItem useEffects can register labels on mount */}
      <div className="py-1 max-h-60 overflow-y-auto">{children}</div>
    </div>
  )

  return createPortal(content, document.body)
}

const SelectItem = forwardRef<
  HTMLDivElement,
  { value: string; children: React.ReactNode; className?: string; label?: string }
>(({ value, children, className, label, ...props }, fwdRef) => {
  const { value: selectedValue, onValueChange, setOpen, registerLabel } = useContext(SelectContext)
  const isSelected = selectedValue === value
  useEffect(() => {
    // Prefer the explicit `label` prop; fall back to children if it is a plain string.
    const displayLabel = label ?? (typeof children === 'string' ? children : undefined)
    if (displayLabel) registerLabel(value, displayLabel)
  }, [value, children, label, registerLabel])
  return (
    <div
      ref={fwdRef}
      className={classNames(
        'flex items-center gap-2 px-4 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground',
        isSelected && 'bg-accent text-accent-foreground',
        className,
      )}
      onClick={(event) => {
        event.stopPropagation()
        onValueChange?.(value)
        setOpen(false)
      }}
      {...props}
    >
      <Check className={classNames('w-4 h-4', !isSelected && 'invisible')} />
      <span>{children}</span>
    </div>
  )
})
SelectItem.displayName = 'SelectItem'

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem }
